<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { until } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { isVoiceCallEnabled } from 'dashboard/helper/inbox';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useCallHistoryStore } from 'dashboard/stores/callHistory';
import { emitter } from 'shared/helpers/mitt';

import CallListItem from 'dashboard/components-next/Calls/CallListItem.vue';
import CallsEmptyState from 'dashboard/components-next/Calls/CallsEmptyState.vue';
import CallsFilterBar from 'dashboard/components-next/Calls/CallsFilterBar.vue';
import { CALL_ACTIVITY_PARAMS } from 'dashboard/components-next/Calls/constants';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';


const RESULTS_PER_PAGE = 25;

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const callHistoryStore = useCallHistoryStore();

const inboxes = useMapGetter('inboxes/getInboxes');
const accountId = useMapGetter('getCurrentAccountId');
const currentUserId = useMapGetter('getCurrentUserID');
const agents = useMapGetter('agents/getVerifiedAgents');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// CallFinder scopes non-admins to their own accepted calls, so the assignee
// filter is only meaningful for admins; everyone else defaults to themselves.
const { isAdmin } = useAdmin();

const voiceInboxes = computed(() => {
  const list = (inboxes.value || []).filter(isVoiceCallEnabled);
  if (list.length > 0) return list;
  return inboxes.value || [];
});

const isVoiceEnabled = computed(() => {
  return isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.FREESWITCH);
});

const calls = computed(() => callHistoryStore.records);
const meta = computed(() => callHistoryStore.meta);
const isFetching = computed(() => callHistoryStore.uiFlags.isFetching);
const accountUiFlags = useMapGetter('accounts/getUIFlags');

const isInitializing = ref(true);

// Filters are seeded from the URL so a shared link restores the same view.
const activity = ref(
  CALL_ACTIVITY_PARAMS[route.query.activity] ? route.query.activity : null
);

const assigneeId = ref(
  isAdmin.value ? Number(route.query.assignee_id) || null : currentUserId.value
);
const inboxId = ref(Number(route.query.inbox_id) || null);
const currentPage = ref(Number(route.query.page) || 1);

const syncFiltersToUrl = () => {
  router.replace({
    query: {
      ...(activity.value && { activity: activity.value }),
      ...(isAdmin.value &&
        assigneeId.value && { assignee_id: assigneeId.value }),
      ...(inboxId.value && { inbox_id: inboxId.value }),
      ...(currentPage.value > 1 && { page: currentPage.value }),
    },
  });
};

const fetchCalls = async () => {
  syncFiltersToUrl();
  try {
    await callHistoryStore.fetchCalls({
      page: currentPage.value,
      ...(CALL_ACTIVITY_PARAMS[activity.value] || {}),
      ...(assigneeId.value ? { agent_id: assigneeId.value } : {}),
      ...(inboxId.value ? { inbox_id: inboxId.value } : {}),
    });
  } catch (error) {
    useAlert(error.message);
  }
};

watch([activity, assigneeId, inboxId], () => {
  currentPage.value = 1;
  fetchCalls();
});

const onPageChange = page => {
  currentPage.value = page;
  fetchCalls();
};

const onVoiceCallEvent = () => {
  fetchCalls();
};

onMounted(async () => {
  emitter.on('voice_call:created', onVoiceCallEvent);
  emitter.on('voice_call:updated', onVoiceCallEvent);
  emitter.on('voice_call:ended', onVoiceCallEvent);

  try {
    await Promise.all([
      store.dispatch('inboxes/get'),
      until(() => accountUiFlags.value.isFetchingItem).toBe(false),
    ]);
    if (!isVoiceEnabled.value) return;
    // Only admins see the assignee filter, so only they need the agent list.
    if (isAdmin.value) store.dispatch('agents/get');
    await fetchCalls();
  } finally {
    isInitializing.value = false;
  }
});

onUnmounted(() => {
  emitter.off('voice_call:created', onVoiceCallEvent);
  emitter.off('voice_call:updated', onVoiceCallEvent);
  emitter.off('voice_call:ended', onVoiceCallEvent);
});
</script>

<template>
  <div
    v-if="isInitializing"
    class="flex flex-col w-full h-full overflow-hidden bg-n-background dark:bg-n-black"
  >
    <div class="h-5 bg-n-background dark:bg-n-black shrink-0 w-full" />
    <div class="flex-1 bg-n-surface-1 rounded-t-2xl flex items-center justify-center">
      <Spinner :size="24" />
    </div>
  </div>
  <CallsEmptyState v-else-if="!isVoiceEnabled" />
  <section
    v-else
    class="flex flex-col w-full h-full overflow-hidden bg-n-background dark:bg-n-black"
  >
    <div class="h-5 bg-n-background dark:bg-n-black shrink-0 w-full" />
    <main class="flex-1 overflow-y-auto px-6 bg-n-surface-1 rounded-t-2xl flex flex-col justify-between min-h-0">
      <div class="w-full mx-auto max-w-5xl flex-grow py-3">
        <header class="flex items-center justify-between w-full h-[3.25rem] mb-2 pb-2">
          <span class="text-xl font-medium truncate text-n-slate-12">
            Call Logs
          </span>
          <div class="flex items-center gap-3">
            <Button
              size="sm"
              icon="i-lucide-phone-call"
              label="Open Dialpad"
              class="cursor-pointer"
              @click="router.push({ name: 'calls_dashboard_dialpad', params: { accountId } })"
            />
          </div>
        </header>

        <CallsFilterBar
          v-model:activity="activity"
          v-model:assignee-id="assigneeId"
          v-model:inbox-id="inboxId"
          class="mb-4 pb-3 border-b border-n-weak"
          :total-count="isFetching ? null : meta.count"
          :agents="agents"
          :inboxes="voiceInboxes"
          :show-assignee="isAdmin"
        />

        <div class="w-full">
          <div v-if="isFetching" class="flex items-center justify-center py-16">
            <Spinner :size="24" />
          </div>
          <div
            v-else-if="!calls.length"
            class="flex items-center justify-center py-16"
          >
            <span class="text-base text-n-slate-11">
              {{ t('CALLS_PAGE.EMPTY_STATE') }}
            </span>
          </div>
          <template v-else>
            <CallListItem v-for="call in calls" :key="call.id" :call="call" />
          </template>
        </div>
      </div>

      <footer v-if="calls.length" class="sticky bottom-0 z-0 pb-4 bg-n-surface-1 w-full max-w-5xl mx-auto">
        <PaginationFooter
          :current-page="currentPage"
          :total-items="meta.count"
          :items-per-page="RESULTS_PER_PAGE"
          @update:current-page="onPageChange"
        />
      </footer>
    </main>
  </section>
</template>
